import type { FC, ReactNode } from 'react'
import type { ServiceId } from '@/types'
import type { EstimatePreset } from '@/components/landing/quote-simulator'

import Head from 'next/head'
import { useCallback, useState } from 'react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { ContactSection } from '@/components/landing/contact-section'
import { HeroSection } from '@/components/landing/hero-section'
import { PricingSection } from '@/components/landing/pricing-section'
import { ProofBand } from '@/components/landing/proof-band'
import { QuoteSimulator } from '@/components/landing/quote-simulator'
import { ReviewsSection } from '@/components/landing/reviews-section'
import { TeamSection } from '@/components/landing/team-section'
import { WorkshopSection } from '@/components/landing/workshop-section'

import { garage } from '@/constants/garage'
import { SHARE_IMAGE, siteConfig } from '@/constants/site'

const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AutoRepair',
    name: garage.name,
    description: siteConfig.description,
    image: SHARE_IMAGE,
    telephone: garage.phone.international,
    priceRange: '€€',
    foundingDate: String(garage.founded),
    address: {
        '@type': 'PostalAddress',
        streetAddress: garage.address.street,
        postalCode: garage.address.postalCode,
        addressLocality: garage.address.city,
        addressCountry: 'FR'
    },
    geo: {
        '@type': 'GeoCoordinates',
        latitude: garage.geo.lat,
        longitude: garage.geo.lon
    },
    openingHoursSpecification: [
        {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '08:00',
            closes: '19:00'
        },
        {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: 'Saturday',
            opens: '09:00',
            closes: '13:00'
        }
    ]
}

const Home: FC = (): ReactNode => {
    const [preset, setPreset] = useState<EstimatePreset | null>(null)

    const handleEstimate = useCallback((id: ServiceId) => {
        setPreset({ id, nonce: Date.now() })
        document.getElementById('devis')?.scrollIntoView({ block: 'start' })
    }, [])

    return (
        <Layout>
            <Seo ogImage={SHARE_IMAGE} />

            <Head>
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
                />
            </Head>

            <HeroSection />
            <ProofBand />
            <PricingSection onEstimate={handleEstimate} />
            <QuoteSimulator preset={preset} />
            <WorkshopSection />
            <ReviewsSection />
            <TeamSection />
            <ContactSection />
        </Layout>
    )
}

export default Home
